import React, { useState } from 'react';
import { toast } from 'sonner';
import PageShell from './PageShell';

const EMAIL_API_URL = process.env.REACT_APP_EMAIL_API_URL || '/api/v1/email/send';
const EMAIL_API_KEY = process.env.REACT_APP_EMAIL_API_KEY || '';

const parseEmails = (value) =>
  (value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const CONTACT_TO = parseEmails(process.env.REACT_APP_CONTACT_TO_EMAILS);
const CONTACT_CC = parseEmails(process.env.REACT_APP_CONTACT_CC_EMAILS);
const CONTACT_BCC = parseEmails(process.env.REACT_APP_CONTACT_BCC_EMAILS);

const escapeHtml = (value) =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

const Contact = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: '' });

  const handleChange = ({ target: { name, value } }) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ type: 'error', message: 'Please fill in your name, email, and message.' });
      return;
    }

    if (!EMAIL_API_KEY) {
      setStatus({ type: 'error', message: 'Email service is not configured yet.' });
      return;
    }

    if (CONTACT_TO.length === 0) {
      setStatus({ type: 'error', message: 'Contact recipient is not configured yet.' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    const safeName = escapeHtml(form.name.trim());
    const safeEmail = escapeHtml(form.email.trim());
    const safeMessage = escapeHtml(form.message.trim()).replaceAll('\n', '<br />');

    try {
      const response = await fetch(EMAIL_API_URL, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-api-key': EMAIL_API_KEY
        },
        body: JSON.stringify({
          to: CONTACT_TO,
          cc: CONTACT_CC,
          bcc: CONTACT_BCC,
          subject: `New contact form inquiry from ${form.name.trim()}`,
          text: `Name: ${form.name.trim()}\nEmail: ${form.email.trim()}\n\nMessage:\n${form.message.trim()}`,
          html: `<p><strong>Name:</strong> ${safeName}</p><p><strong>Email:</strong> ${safeEmail}</p><p><strong>Message:</strong><br />${safeMessage}</p>`
        })
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        const errorMessage = data?.errors?.join(', ') || data?.message || 'Failed to send message.';
        throw new Error(errorMessage);
      }

      setForm({ name: '', email: '', message: '' });
      setStatus({ type: 'success', message: data?.message || 'Message sent successfully.' });
      toast.success('Message sent successfully.');
    } catch (error) {
      const message = error.message || 'Failed to send message.';
      setStatus({ type: 'error', message });
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell
      title="Contact"
      seoTitle="Contact WealthDost"
      seoDescription="Contact WealthDost for partnerships, support, or general inquiries."
      seoPath="/contact"
    >
      <p className="text-[#94A3B8]">
        For inquiries, partnerships, or support, please reach out and we will get back to you shortly.
      </p>
      <form onSubmit={handleSubmit} className="mt-6 rounded-2xl border border-[#1E293B] bg-[#0F172A] p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input
            type="text"
            name="name"
            placeholder="Full name"
            value={form.name}
            onChange={handleChange}
            className="w-full rounded-lg bg-[#0B1220] border border-[#1E293B] px-4 py-3 text-[#F8FAFC] placeholder:text-[#64748B]"
          />
          <input
            type="email"
            name="email"
            placeholder="Email address"
            value={form.email}
            onChange={handleChange}
            className="w-full rounded-lg bg-[#0B1220] border border-[#1E293B] px-4 py-3 text-[#F8FAFC] placeholder:text-[#64748B]"
          />
        </div>
        <textarea
          rows={5}
          name="message"
          placeholder="How can we help?"
          value={form.message}
          onChange={handleChange}
          className="mt-4 w-full rounded-lg bg-[#0B1220] border border-[#1E293B] px-4 py-3 text-[#F8FAFC] placeholder:text-[#64748B]"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="mt-4 inline-flex items-center justify-center rounded-full bg-[#2563EB] px-6 py-2.5 text-sm font-medium text-white shadow-[0_0_20px_rgba(37,99,235,0.3)]"
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
        {status.message && (
          <p className={`mt-3 text-sm ${status.type === 'error' ? 'text-[#FCA5A5]' : 'text-[#86EFAC]'}`}>
            {status.message}
          </p>
        )}
      </form>
    </PageShell>
  );
};

export default Contact;
